// Copy for the Retail Automation Suite page (src/pages/Solutions.tsx, /solutions/retail-automation-suite).
// Logo alt texts (GA4, SAP, Shopify, …) are brand names and stay in the page.

const en = {
  seoTitle: 'Retail Automation Suite | Stratodata',
  seoDescription: 'A unified platform for retail data: automated pricing, profit-based bidding, feed management and AI analytics — all built on a single verified data foundation.',
  hero: {
    eyebrow: 'Solutions',
    title: 'Retail Automation Suite (RAS)',
    tagline: 'Moving Beyond Reporting. Designed for Net Profit.',
    subtitle: 'The only data solution SMB retailers need.',
    cta: 'Book a demo call',
    imageAlt: 'RAS Console – Analytics Hub dashboard',
  },
  challengeBadge: 'Challenge',
  solution: {
    badge: 'Solution',
    title: 'RAS – The Unified Approach',
    subtitle: 'RAS addresses these challenges by bringing data foundation, strategic guidance, and automation under one umbrella.',
  },
  dataModel: {
    title: 'Unified Retail Data Model',
    p1: 'Everything starts here. We build a Standard Verified Omni-Channel Retail Data Model for you.',
    p2: 'Leveraging our experience building dozens of successful models, we know exactly which sources (ERP, e-commerce, marketing, WMS) to connect and how to blend them to avoid "garbage in, garbage out".',
    p3: 'Your marketing, web analytics, ERP and stock data in one place, refreshed daily, so every decision starts from the same numbers.',
  },
  northStar: {
    title: 'Strategic North Star Tracking',
    imageAlt: 'Strategic North Star Tracking dashboard',
    p1: 'We don\'t just give you a dashboard; we give you a compass. Our reporting is specifically tailored to track your North Star (Net Profit).',
    p2: 'You can view your North Star from the point of view of sales channels, marketing channels and ecommerce funnel as well as products, product categories and brands.',
    p3: 'Our consultants ensure your leadership and growing team are aligned, optimising every effort toward this single KPI rather than conflicting departmental goals.',
  },
  automation: {
    title: 'Off-the-Shelf Automation',
    imageAlt: 'Off-the-Shelf Automation',
    items: [
      'Autopricing: Automatic daily pricing based on margin and inventory, with easy aggressiveness control.',
      'Automatic Bidding & PPC: Data-driven campaign automation for maximum ROI.',
      'Product Recommender: Sort products by demand, margin, inventory or newness.',
      'Autoforecaster: Daily updated sales forecasts with manual adjustments.',
      'SBA Automation: Automatic seasonal PPC adjustments from one central location.',
    ],
  },
  aiDataset: {
    title: 'Retail context engineered dataset for AI',
    imageAlt: 'Retail context engineered dataset for AI',
    items: [
      'Connect your dataset to your AI solution of choice.',
      'Context engineered for AI analysis precision.',
      'Database of sample questions and analysis to run.',
      'Dive deep with consistency: reports and AI work on same data.',
    ],
  },
  cta: {
    title: 'Ready to Take Control of Your Data?',
    text: 'Every day without a unified data model is another day guessing at your results.',
    button: 'See RAS on your numbers',
  },
};

const sk: typeof en = {
  seoTitle: 'Retail Automation Suite | Stratodata',
  seoDescription: 'Jednotná platforma pre retailové dáta: automatická cenotvorba, bidovanie podľa zisku, správa feedov a AI analytika. Všetko stojí na jednom overenom dátovom základe.',
  hero: {
    eyebrow: 'Riešenia',
    title: 'Retail Automation Suite (RAS)',
    tagline: 'Viac než reporting. Navrhnuté pre čistý zisk.',
    subtitle: 'Jediné dátové riešenie, ktoré malí a stredne veľkí retaileri potrebujú.',
    cta: 'Rezervovať demo ukážku',
    imageAlt: 'Dashboard Analytics Hub v RAS Console',
  },
  challengeBadge: 'Výzva',
  solution: {
    badge: 'Riešenie',
    title: 'RAS – jednotný prístup',
    subtitle: 'RAS tieto výzvy rieši tak, že dátový základ, strategické vedenie a automatizáciu spojí pod jednu strechu.',
  },
  dataModel: {
    title: 'Jednotný retailový dátový model',
    p1: 'Všetko začína tu. Postavíme vám čistý a overený dátový model, ktorý spojí všetky vaše predajné kanály na jednom mieste.',
    p2: 'Postavili sme už desiatky úspešných modelov, takže presne vieme, ktoré zdroje (ERP, e-shop, marketing, WMS) prepojiť a ako ich skombinovať, aby neplatilo „garbage in, garbage out“.',
    p3: 'Dáta z marketingu, webovej analytiky, ERP a skladu budete mať na jednom mieste a aktualizované každý deň, takže každé rozhodnutie vychádza z rovnakých čísel.',
  },
  northStar: {
    title: 'Strategické sledovanie North Star metriky',
    imageAlt: 'Dashboard so sledovaním North Star metriky',
    p1: 'Nedávame vám len dashboard, dávame vám kompas. Náš reporting je postavený tak, aby sledoval vašu North Star metriku, teda čistý zisk.',
    p2: 'Svoju North Star metriku uvidíte z pohľadu predajných kanálov, marketingových kanálov a e-commerce funnelu, ale aj produktov, produktových kategórií a značiek.',
    p3: 'Naši konzultanti sa postarajú, aby vedenie aj rastúci tím ťahali za jeden koniec. Každé úsilie tak smeruje k jedinému KPI, nie k protichodným cieľom jednotlivých oddelení.',
  },
  automation: {
    title: 'Automatizácia pripravená na použitie',
    imageAlt: 'Automatizácia pripravená na použitie',
    items: [
      'Autopricing – Automatická denná cenotvorba podľa marže a zásob s jednoduchým nastavením agresivity.',
      'Automatické bidovanie a PPC – Kampane riadené dátami pre maximálnu návratnosť.',
      'Product Recommender – Radenie produktov podľa dopytu, marže, zásob alebo novosti.',
      'Autoforecaster – Denne aktualizované prognózy predaja, ktoré môžete ručne upraviť.',
      'SBA automatizácia – Automatické sezónne úpravy PPC z jedného centrálneho miesta.',
    ],
  },
  aiDataset: {
    title: 'Retailový dataset pre AI s pripraveným kontextom',
    imageAlt: 'Retailový dataset pre AI s pripraveným kontextom',
    items: [
      'Napojte svoje dáta na AI riešenie podľa vlastného výberu.',
      'Kontext pripravený tak, aby AI analýzy boli presné.',
      'Databáza vzorových otázok a analýz, ktoré môžete spustiť.',
      'Choďte do hĺbky bez nezrovnalostí: reporty aj AI pracujú s tými istými dátami.',
    ],
  },
  cta: {
    title: 'Pripravení mať dáta pod kontrolou?',
    text: 'Každý deň bez jednotného dátového modelu je ďalší deň, keď svoje výsledky len odhadujete.',
    button: 'Pozrite si RAS na vašich číslach',
  },
};

export const rasCopy = { en, sk };
